import { useEffect, useRef, useState } from 'react'
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'

interface OtpInputProps {
  /** Anzahl der Einzelfelder (Default: 6). */
  length?: number
  /** Aktueller OTP-Wert (kontrolliert, Länge ≤ `length`). */
  value: string
  /** Wird bei jeder Änderung mit dem zusammengesetzten String aufgerufen. */
  onChange: (value: string) => void
  /** Wird aufgerufen, sobald alle `length` Stellen gefüllt sind. */
  onComplete?: (value: string) => void
}

/** Zerlegt den OTP-String in ein Array fester Länge (fehlende Stellen = ''). */
function toDigits(value: string, length: number): string[] {
  return Array.from({ length }, (_, i) => value[i] ?? '')
}

/** Ziffern-Eingabe für Einmalpasswörter (OTP): `length` Einzelfelder, die über
 *  value/onChange als ein zusammenhängender String gesteuert werden. Tippen
 *  rückt den Fokus vor, Backspace in einem leeren Feld springt zurück und
 *  löscht die vorherige Ziffer, Backspace in einem gefüllten Feld löscht nur
 *  diese Position (ohne die nachfolgenden Ziffern zu verschieben), Paste
 *  verteilt eine mehrstellige Zahl ab der aktuellen Position. Nicht-numerische
 *  Eingaben werden ignoriert.
 *
 *  Intern wird `digits` (Array fester Länge) als lokaler Zustand geführt und
 *  nur dann aus der `value`-Prop resynchronisiert, wenn deren kompakte Form
 *  vom aktuellen `digits.join('')` abweicht (z.B. externer Reset durch den
 *  Parent). So bleiben eigene In-Place-Edits (Löschen einer mittleren Stelle)
 *  positionsstabil, während die Komponente kontrolliert bleibt. */
export function OtpInput({ length = 6, value, onChange, onComplete }: OtpInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const [digits, setDigits] = useState<string[]>(() => toDigits(value, length))

  useEffect(() => {
    if (value !== digits.join('')) {
      setDigits(toDigits(value, length))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, length])

  const focusBox = (index: number) => {
    inputRefs.current[index]?.focus()
  }

  /** Übernimmt die neuen Digits als lokalen Zustand, meldet den zusammen-
   *  gesetzten Wert an den Parent und löst bei Vollständigkeit onComplete aus. */
  const commit = (nextDigits: string[]) => {
    setDigits(nextDigits)
    const next = nextDigits.join('')
    onChange(next)
    if (nextDigits.every((d) => d !== '')) {
      onComplete?.(next)
    }
  }

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    const digit = raw.replace(/\D/g, '').slice(-1)
    if (raw !== '' && digit === '') {
      // Nicht-numerische Eingabe: Feld bleibt unverändert.
      return
    }
    const chars = [...digits]
    chars[index] = digit
    commit(chars)
    if (digit && index < length - 1) {
      focusBox(index + 1)
    }
  }

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') return
    if (digits[index]) {
      // Gefülltes Feld: nur diese Position löschen, Fokus bleibt.
      event.preventDefault()
      const chars = [...digits]
      chars[index] = ''
      commit(chars)
      return
    }
    if (index > 0) {
      // Leeres Feld: vorherige Position löschen und Fokus zurückspringen.
      event.preventDefault()
      const chars = [...digits]
      chars[index - 1] = ''
      commit(chars)
      focusBox(index - 1)
    }
  }

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '')
    if (!pasted) return
    const chars = [...digits]
    let cursor = index
    for (const char of pasted) {
      if (cursor >= length) break
      chars[cursor] = char
      cursor += 1
    }
    commit(chars)
    focusBox(Math.min(cursor, length - 1))
  }

  return (
    <div className="otp-input">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          className="otp-box"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste(index)}
          aria-label={`Ziffer ${index + 1}`}
        />
      ))}
    </div>
  )
}
